import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DateInput,
  ErrorText,
  Field,
  Input,
  LocalizedInput,
  Modal,
  Select,
  type Localized,
} from '@/components/ui';

type LinkType = 'PRODUCT' | 'CATEGORY' | 'URL' | 'NONE';

interface Banner {
  id: string;
  title: Localized | null;
  subtitle: Localized | null;
  imageUrl: string;
  storageId: string | null;
  linkType: LinkType;
  linkValue: string | null;
  sortOrder: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
}

interface Draft {
  id?: string;
  title: Localized;
  subtitle: Localized;
  imageUrl: string;
  storageId?: string;
  linkType: LinkType;
  linkValue: string;
  sortOrder: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
}

interface CategoryNode {
  id: string;
  level: number;
  name: Localized;
  children: CategoryNode[];
}

interface ProductRow {
  id: string;
  name: Localized;
  code: string;
}

const EMPTY: Draft = {
  title: {},
  subtitle: {},
  imageUrl: '',
  linkType: 'NONE',
  linkValue: '',
  sortOrder: '0',
  startsAt: '',
  endsAt: '',
  isActive: true,
};

export default function BannersPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Banner | null>(null);
  const [error, setError] = useState<string | null>(null);

  const banners = useQuery({
    queryKey: ['banners-admin'],
    queryFn: async () => (await api.get<Banner[]>('/admin/banners')).data,
  });

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/admin/categories')).data,
  });

  const products = useQuery({
    queryKey: ['products', 'for-banner'],
    queryFn: async () =>
      (await api.get<{ items: ProductRow[] }>('/admin/products', { params: { limit: 60 } })).data
        .items,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['banners-admin'] });
  const fail = (e: unknown) => setError(errorMessage(e));

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const payload = {
        title: value.title,
        subtitle: value.subtitle,
        imageUrl: value.imageUrl,
        storageId: value.storageId,
        linkType: value.linkType,
        linkValue: value.linkType === 'NONE' ? undefined : value.linkValue,
        sortOrder: Number(value.sortOrder || 0),
        // Пустые даты означают «показывать всегда»
        startsAt: value.startsAt ? `${value.startsAt}T00:00:00.000Z` : null,
        endsAt: value.endsAt ? `${value.endsAt}T23:59:59.000Z` : null,
        isActive: value.isActive,
      };

      return value.id
        ? api.patch(`/admin/banners/${value.id}`, payload)
        : api.post('/admin/banners', payload);
    },
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (banner: Banner) =>
      api.patch(`/admin/banners/${banner.id}`, { isActive: !banner.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (banner: Banner) => api.delete(`/admin/banners/${banner.id}`),
    onSuccess: () => {
      setConfirmDelete(null);
      void invalidate();
    },
    onError: fail,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append('file', file);

      const { data } = await api.post<{ id: string; url: string }>(
        '/admin/media?folder=banners',
        body,
      );
      return data;
    },
    onSuccess: (media) =>
      setDraft((prev) => (prev ? { ...prev, imageUrl: media.url, storageId: media.id } : prev)),
    onError: fail,
  });

  const flat = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => [n, ...flat(n.children)]);

  const period = (banner: Banner) => {
    if (!banner.startsAt && !banner.endsAt) return 'всегда';
    const from = banner.startsAt ? new Date(banner.startsAt).toLocaleDateString('ru-RU') : '…';
    const to = banner.endsAt ? new Date(banner.endsAt).toLocaleDateString('ru-RU') : '…';
    return `${from} — ${to}`;
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Акции</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Баннеры в верхнем блоке главной страницы приложения
          </p>
        </div>
        <Button onClick={() => setDraft({ ...EMPTY })}>Новая акция</Button>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Card>
        {banners.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (banners.data ?? []).length === 0 ? (
          <p className="text-sm text-neutral-500">Акций пока нет</p>
        ) : (
          <div className="space-y-2">
            {(banners.data ?? []).map((banner) => (
              <div
                key={banner.id}
                className="flex items-center gap-4 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800"
              >
                <img
                  src={banner.imageUrl}
                  alt=""
                  className="h-16 w-28 shrink-0 rounded-lg bg-neutral-100 object-cover dark:bg-neutral-800"
                />

                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium">
                    {banner.title?.ru ?? 'Без заголовка'}{' '}
                    {banner.isActive ? (
                      <Badge tone="green">показывается</Badge>
                    ) : (
                      <Badge tone="red">скрыта</Badge>
                    )}
                  </p>
                  {banner.subtitle?.ru && <p className="text-neutral-500">{banner.subtitle.ru}</p>}
                  <p className="text-xs text-neutral-400">
                    период: {period(banner)} · порядок {banner.sortOrder} ·{' '}
                    {banner.linkType === 'NONE' ? 'без перехода' : `ведёт на ${banner.linkType}`}
                  </p>
                </div>

                <div className="shrink-0">
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setDraft({
                        id: banner.id,
                        title: banner.title ?? {},
                        subtitle: banner.subtitle ?? {},
                        imageUrl: banner.imageUrl,
                        storageId: banner.storageId ?? undefined,
                        linkType: banner.linkType,
                        linkValue: banner.linkValue ?? '',
                        sortOrder: String(banner.sortOrder),
                        startsAt: banner.startsAt?.slice(0, 10) ?? '',
                        endsAt: banner.endsAt?.slice(0, 10) ?? '',
                        isActive: banner.isActive,
                      })
                    }
                  >
                    Изм.
                  </Button>
                  <Button variant="ghost" onClick={() => toggle.mutate(banner)}>
                    {banner.isActive ? 'Скрыть' : 'Показать'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-red-600"
                    onClick={() => setConfirmDelete(banner)}
                  >
                    Удал.
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(draft)}
        title={draft?.id ? 'Изменить акцию' : 'Новая акция'}
        onClose={() => setDraft(null)}
      >
        {draft && (
          <div className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium">Изображение</span>

              {draft.imageUrl ? (
                <img src={draft.imageUrl} alt="" className="h-32 w-full rounded-lg object-cover" />
              ) : (
                <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-neutral-300 text-sm text-neutral-400 dark:border-neutral-700">
                  Загрузите картинку
                </div>
              )}

              <label className="block">
                <span className="inline-block cursor-pointer rounded-lg border border-neutral-300 px-3 py-1.5 text-sm transition hover:bg-neutral-100 dark:border-neutral-700 dark:hover:bg-neutral-800">
                  {upload.isPending ? 'Загружаем…' : 'Выбрать файл'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) upload.mutate(file);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            <LocalizedInput
              label="Заголовок"
              value={draft.title}
              onChange={(title) => setDraft({ ...draft, title })}
            />
            <LocalizedInput
              label="Подзаголовок"
              value={draft.subtitle}
              onChange={(subtitle) => setDraft({ ...draft, subtitle })}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Куда ведёт">
                <Select
                  value={draft.linkType}
                  onChange={(e) =>
                    setDraft({ ...draft, linkType: e.target.value as LinkType, linkValue: '' })
                  }
                >
                  <option value="NONE">Никуда</option>
                  <option value="CATEGORY">В категорию</option>
                  <option value="PRODUCT">На товар</option>
                  <option value="URL">На ссылку</option>
                </Select>
              </Field>

              {draft.linkType === 'CATEGORY' && (
                <Field label="Категория">
                  <Select
                    value={draft.linkValue}
                    onChange={(e) => setDraft({ ...draft, linkValue: e.target.value })}
                  >
                    <option value="">Выберите</option>
                    {flat(categories.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {'— '.repeat(c.level - 1)}
                        {c.name.ru}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {draft.linkType === 'PRODUCT' && (
                <Field label="Товар">
                  <Select
                    value={draft.linkValue}
                    onChange={(e) => setDraft({ ...draft, linkValue: e.target.value })}
                  >
                    <option value="">Выберите</option>
                    {(products.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name.ru}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {draft.linkType === 'URL' && (
                <Field label="Ссылка">
                  <Input
                    value={draft.linkValue}
                    onChange={(e) => setDraft({ ...draft, linkValue: e.target.value })}
                    placeholder="https://"
                  />
                </Field>
              )}

              <Field label="Показывать с" hint="Пусто — с самого начала">
                <DateInput
                  value={draft.startsAt}
                  onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
                />
              </Field>

              <Field label="Показывать по" hint="Пусто — бессрочно">
                <DateInput
                  value={draft.endsAt}
                  onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
                />
              </Field>

              <Field label="Порядок" hint="Меньше — левее в ленте">
                <Input
                  value={draft.sortOrder}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, sortOrder: e.target.value })}
                />
              </Field>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDraft(null)}>
                Отмена
              </Button>
              <Button
                disabled={save.isPending || !draft.imageUrl}
                onClick={() => save.mutate(draft)}
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Удалить акцию?"
        description="Баннер исчезнет с главной страницы приложения."
        confirmLabel="Удалить"
        tone="danger"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
      />
    </div>
  );
}
